/* eslint-disable no-shadow */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { addDays, addMinutes } from 'date-fns';
import { DrawerLayoutWrapper } from 'common';
import ProgressBar from 'feature/dispatching/components/ProgressBar';
import { isOptionEqualToValue } from 'feature/dispatching/utils';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { formatISO, setSeconds } from 'date-fns/fp';
import { initialCompartmentState, initialSupplierState } from '../components/wizard/initialState';
import {
  useGetDriversQuery,
  useGetLocationsQuery,
  useGetTrailersQuery,
  useLazyValidatePoNumberQuery,
} from '../loadsEndpoints';
import LoadingTextField from '../components/wizard/LoadingTextField';

const theme = createTheme({
  components: {
    MuiPickersToolbar: {
      styleOverrides: {
        content: {
          alignItems: 'flex-end',
        },
        root: {
          display: 'flex',
        },
      },
    },
  },
  palette: {
    primary: {
      main: '#267E4D',
    },
  },
});

const DATE_MESSAGES = {
  INVALID_TIME: 'Invalid Time',
  MAX_END_TIME: 'End Time cannot be more than 30 days after Start Time',
  MAX_START_TIME: 'Start Time must be before End Time',
  MIN_END_TIME: 'End Time must be after Start Time',
  MIN_START_TIME: 'Start Time cannot be in the past',
};

// prettier-ignore
const isMinStartTimeInvalid = startTime => 
  startTime 
    ? startTime <= formatISO(new Date()) 
    : false;

// prettier-ignore
const isMaxStartTimeInvalid = (startTime, endTime) => 
  startTime && endTime 
    ? startTime >= endTime 
    : false;

const isMinEndTimeInvalid = (startTime, endTime) =>
  startTime && endTime
    ? endTime <= startTime
    : endTime
    ? endTime <= formatISO(addMinutes(new Date(), 1))
    : false;

const isMaxEndTimeInvalid = (startTime, endTime) =>
  startTime && endTime
    ? endTime > formatISO(addDays(new Date(startTime), 30))
    : endTime
    ? endTime > formatISO(addDays(addMinutes(new Date(), 1), 30))
    : false;

const isNotEmpty = R.compose(R.not, R.isEmpty);

const pickerPaperStyles = {
  sx: {
    border: 1,
    borderColor: 'graphic.light',
    borderRadius: 0,
    'span.MuiTypography-root': {
      color: 'inherit',
    },
  },
};

const LoadStep = ({ onClose, next, loadState, setLoadState, isFetchingLoad }) => {
  const {
    poNumber,
    deliveryStartDateTime,
    deliveryEndDateTime,
    location,
    driver,
    trailer,
    fillAll,
  } = loadState;

  const {
    data: locationsQuery = [],
    isFetching: isFetchingLocations,
    isSuccess: isLocationsSuccess,
  } = useGetLocationsQuery();

  const { data: driversQuery = [], isFetching: isFetchingDrivers } = useGetDriversQuery(
    { locationId: R.prop('locationId', location) },
    { skip: R.isNil(location) }
  );

  const { data: trailersQuery = [], isFetching: isFetchingTrailers } = useGetTrailersQuery(
    { locationId: R.prop('locationId', location) },
    { skip: R.isNil(location) }
  );

  const [triggerValidatePoNumber] = useLazyValidatePoNumberQuery();

  const [validationState, setValidationState] = useState({
    deliveryEndDateTime: '',
    deliveryStartDateTime: '',
    poNumber: '',
  });

  const originalPoNumber = useRef('');

  const isInvalid = [
    R.isEmpty(poNumber),
    isNotEmpty(validationState.poNumber),
    R.isNil(deliveryStartDateTime),
    R.isNil(deliveryEndDateTime),
    isNotEmpty(validationState.deliveryStartDateTime),
    isNotEmpty(validationState.deliveryEndDateTime),
    R.isNil(location),
    R.isNil(driver),
    R.isNil(trailer),
  ].includes(true);

  const validatePoNumber = poNumber => {
    if (R.isEmpty(poNumber)) {
      setValidationState(R.assoc('poNumber', 'PO Number required'));
    } else if (R.equals(poNumber, originalPoNumber.current)) {
      // don't validate the original value. It'll always fail
      setValidationState(R.assoc('poNumber', ''));
    } else {
      // server validation
      triggerValidatePoNumber({ poNumber })
        .unwrap()
        .then(({ isValid }) => {
          const message = isValid ? '' : 'This PO# is already associated with a load';
          setValidationState(R.assoc('poNumber', message));
        });
    }
  };

  const handlePoNumberChange = event => {
    const { value } = event.target;
    validatePoNumber(value);
    setLoadState(R.assoc('poNumber', value));
  };

  const validateDeliveryStartDateTime = time => {
    if (R.isNil(time)) {
      setValidationState(R.assoc('deliveryStartDateTime', DATE_MESSAGES.INVALID_TIME));
    } else if (isMinStartTimeInvalid(time)) {
      setValidationState(R.assoc('deliveryStartDateTime', DATE_MESSAGES.MIN_START_TIME));
    } else if (isMaxStartTimeInvalid(time, deliveryEndDateTime)) {
      setValidationState(R.assoc('deliveryStartDateTime', DATE_MESSAGES.MAX_START_TIME));
    } else {
      setValidationState(R.assoc('deliveryStartDateTime', ''));
      if (isMaxEndTimeInvalid(time, deliveryEndDateTime)) {
        setValidationState(R.assoc('deliveryEndDateTime', DATE_MESSAGES.MAX_END_TIME));
      } else if (
        [DATE_MESSAGES.MIN_END_TIME, DATE_MESSAGES.MAX_END_TIME].includes(
          validationState.deliveryEndDateTime
        )
      ) {
        setValidationState(R.assoc('deliveryEndDateTime', ''));
      }
    }
  };

  const handleDeliveryStartDateTimeChange = value => {
    // prettier-ignore
    const time = R.tryCatch(
      R.o(formatISO, setSeconds(0)),
      R.always(null)
    )(value);

    validateDeliveryStartDateTime(time);
    setLoadState(R.assoc('deliveryStartDateTime', time));
  };

  const validateDeliveryEndDateTime = time => {
    if (R.isNil(time)) {
      setValidationState(R.assoc('deliveryEndDateTime', DATE_MESSAGES.INVALID_TIME));
    } else if (isMinEndTimeInvalid(deliveryStartDateTime, time)) {
      setValidationState(R.assoc('deliveryEndDateTime', DATE_MESSAGES.MIN_END_TIME));
    } else if (isMaxEndTimeInvalid(deliveryStartDateTime, time)) {
      setValidationState(R.assoc('deliveryEndDateTime', DATE_MESSAGES.MAX_END_TIME));
    } else {
      setValidationState(R.assoc('deliveryEndDateTime', ''));
      if (validationState.deliveryStartDateTime === DATE_MESSAGES.MAX_START_TIME)
        setValidationState(R.assoc('deliveryStartDateTime', ''));
    }
  };

  const handleDeliveryEndDateTimeChange = value => {
    // prettier-ignore
    const time = R.tryCatch(
      R.o(formatISO, setSeconds(0)),
      R.always(null)
    )(value);

    validateDeliveryEndDateTime(time);
    setLoadState(R.assoc('deliveryEndDateTime', time));
  };

  const handleLocationChange = (_, value) => {
    setLoadState(
      R.compose(
        R.mergeLeft(initialSupplierState),
        R.assoc('trailer', null),
        R.assoc('driver', null),
        R.assoc('location', value)
      )
    );
  };

  // prettier-ignore
  const handleDriverChange = (_, value) => {
    const transformations = {
      compartments: R.map(R.mergeLeft(initialCompartmentState))
    }
    setLoadState(
      R.compose(
        R.evolve(transformations),
        R.assoc('driver', value)
      )
    );
  };

  const handleTrailerChange = (_, value) => {
    const sumVolume = (total, compartment) => total + compartment.capacityInGallons;
    const fillCompartments = {
      ...initialCompartmentState,
      capacityInGallons: value.compartments.reduce(sumVolume, 0),
      compartmentId: 0,
    };
    const indexCompartments = R.compose(
      R.indexBy(R.prop('compartmentId')),
      R.map(R.mergeLeft(initialCompartmentState))
    );
    const defaultCompartments = fillAll
      ? { 0: fillCompartments }
      : indexCompartments(value.compartments);

    setLoadState(
      R.compose(R.assoc('compartments', defaultCompartments), R.assoc('trailer', value))
    );
  };

  const getMinEndTime = () => {
    // prettier-ignore
    const dateTime = deliveryStartDateTime 
      ? new Date(deliveryStartDateTime) 
      : new Date();
    return addMinutes(dateTime, 1);
  };

  const getMaxEndTime = () => {
    // prettier-ignore
    const dateTime = deliveryStartDateTime 
      ? new Date(deliveryStartDateTime) 
      : new Date();
    return addDays(dateTime, 30);
  };

  useEffect(() => {
    if (poNumber && !originalPoNumber.current) {
      originalPoNumber.current = poNumber;
    }
  }, [poNumber]);

  useEffect(() => {
    if (deliveryStartDateTime && deliveryEndDateTime) {
      validateDeliveryStartDateTime(deliveryStartDateTime);
      validateDeliveryEndDateTime(deliveryEndDateTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryStartDateTime, deliveryEndDateTime]);

  const buttons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button disabled={isInvalid} onClick={next} sx={{ width: 150 }}>
        Continue
      </Button>
    </Box>
  );

  return (
    <DrawerLayoutWrapper
      buttons={buttons}
      headerButtonText="CLOSE"
      isLoading={isFetchingLoad}
      onHeaderButtonClick={onClose}
      title={loadState.poNumber ? 'Edit Load' : 'New Load'}
    >
      <ProgressBar label="Load (1 of 4)" value={25} />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack spacing={2} sx={{ height: '700px', p: 3 }}>
          <Typography variant="body1">Load Information</Typography>

          <TextField
            error={isNotEmpty(validationState.poNumber)}
            fullWidth
            helperText={validationState.poNumber}
            label="Internal PO Number"
            onChange={handlePoNumberChange}
            slotProps={{
              htmlInput: { maxLength: '10' },
            }}
            value={poNumber}
          />
          <ThemeProvider theme={theme}>
            <DateTimePicker
              label="Delivery Start Time"
              minDateTime={new Date()}
              minutesStep={5}
              onChange={handleDeliveryStartDateTimeChange}
              slotProps={{
                textField: {
                  error: isNotEmpty(validationState.deliveryStartDateTime),
                  helperText: validationState.deliveryStartDateTime,
                },
              }}
              value={deliveryStartDateTime ? new Date(deliveryStartDateTime) : null}
            />

            <DateTimePicker
              label="Delivery End Time"
              maxDateTime={getMaxEndTime()}
              minDateTime={getMinEndTime()}
              minutesStep={5}
              onChange={handleDeliveryEndDateTimeChange}
              slotProps={{
                desktopPaper: pickerPaperStyles,
                textField: {
                  error: isNotEmpty(validationState.deliveryEndDateTime),
                  helperText: validationState.deliveryEndDateTime,
                },
              }}
              value={deliveryEndDateTime ? new Date(deliveryEndDateTime) : null}
            />
          </ThemeProvider>

          <Autocomplete
            disableClearable
            disabled={!isLocationsSuccess}
            getOptionLabel={l => `${l.locationId} ${l.name.toUpperCase()}`}
            isOptionEqualToValue={isOptionEqualToValue('locationId')}
            loading={isFetchingLocations}
            onChange={handleLocationChange}
            options={locationsQuery}
            renderInput={params => (
              <LoadingTextField
                label="Terminal Location"
                loading={isFetchingLocations}
                params={params}
              />
            )}
            value={location}
          />

          <Autocomplete
            disableClearable
            disabled={R.isNil(location)}
            getOptionLabel={d => `${d.lastName}, ${d.firstName}`}
            isOptionEqualToValue={isOptionEqualToValue('driverId')}
            loading={isFetchingDrivers}
            onChange={handleDriverChange}
            options={driversQuery}
            renderInput={params => (
              <LoadingTextField label="Driver" loading={isFetchingDrivers} params={params} />
            )}
            value={driver}
          />

          <Autocomplete
            disableClearable
            disabled={R.isNil(location)}
            getOptionLabel={R.prop('vaporTightnessCode')}
            isOptionEqualToValue={isOptionEqualToValue('trailerId')}
            loading={isFetchingTrailers}
            onChange={handleTrailerChange}
            options={trailersQuery}
            renderInput={params => (
              <LoadingTextField label="Trailer ID" loading={isFetchingTrailers} params={params} />
            )}
            value={trailer}
          />
        </Stack>
      </LocalizationProvider>
    </DrawerLayoutWrapper>
  );
};

LoadStep.propTypes = {
  isFetchingLoad: PropTypes.bool.isRequired,
  loadState: PropTypes.shape({
    deliveryEndDateTime: PropTypes.string,
    deliveryStartDateTime: PropTypes.string,
    driver: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    fillAll: PropTypes.bool,
    location: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    poNumber: PropTypes.string,
    trailer: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  }).isRequired,
  next: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  setLoadState: PropTypes.func.isRequired,
};

export default LoadStep;
